import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const DISMISS_KEY = "liq_profile_banner_dismissed";

/**
 * ProfileSetupBanner — prompts Growth+ users without a company profile to
 * set one up. Self-dismisses + persists dismissal in localStorage.
 */
export default function ProfileSetupBanner() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user) return;
    const tier = (user.tier || "free").toLowerCase();
    const eligible = ["growth", "scale", "enterprise"].includes(tier);
    if (!eligible) return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
    let cancelled = false;
    http
      .get("/profile")
      .then(({ data }) => {
        if (!cancelled && !data.profile) setShow(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!show) return null;

  return (
    <div
      data-testid="profile-setup-banner"
      className="bg-ink-900 text-white rounded-sm p-4 mb-6 flex items-start sm:items-center gap-3 sm:gap-4 flex-wrap"
    >
      <div className="grid place-items-center w-9 h-9 rounded-sm bg-white/10 shrink-0">
        <Sparkles className="w-4 h-4 text-brand" />
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-white/50">
          New · AI Fit Scoring
        </div>
        <div className="text-sm font-semibold mt-0.5">
          Score every lead against <span className="text-brand">your</span> ICP — not just generic interest.
        </div>
        <div className="text-xs text-white/60 mt-0.5">
          Tell LeadIQ what you sell — takes ~2 minutes.
        </div>
      </div>
      <Link
        to="/settings/profile"
        data-testid="profile-setup-banner-cta"
        className="inline-flex items-center gap-1 bg-brand hover:bg-brand-dark text-white text-[11px] uppercase tracking-widestx font-bold px-3 py-2 rounded-sm transition-colors"
      >
        Set up profile
        <ArrowRight className="w-3 h-3" />
      </Link>
      <button
        data-testid="profile-setup-banner-dismiss"
        onClick={() => {
          localStorage.setItem(DISMISS_KEY, "1");
          setShow(false);
        }}
        className="text-white/40 hover:text-white p-1"
        title="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
